import React, { useCallback, useRef, useState } from 'react';
import { Modal, Button, Space, message, Typography, Tag } from 'antd';
import { PrinterOutlined, CloseOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { POSTransaction } from '../../types/pos';
import { ReceiptPrintView } from './ReceiptPrintView';
import { executeReceiptPrint } from '../../utils/printPosReceipt';
import { formatNu } from '../../utils/formatAmount';

export type ReceiptPrintModalProps = {
  open: boolean;
  transaction: POSTransaction | null;
  storeName: string;
  onClose: () => void;
};

export const ReceiptPrintModal: React.FC<ReceiptPrintModalProps> = ({
  open,
  transaction,
  storeName,
  onClose,
}) => {
  const previewRef = useRef<HTMLDivElement>(null);
  const printingRef = useRef(false);
  const [printing, setPrinting] = useState(false);

  const handleDoneNoPrint = useCallback(() => {
    message.success('Sale kept on record — no receipt printed');
    onClose();
  }, [onClose]);

  const handlePrint = useCallback(async () => {
    if (!transaction || printingRef.current) return false;

    const previewEl = previewRef.current?.querySelector('.receipt-print-document');
    if (!previewEl) {
      message.error('Receipt preview is not ready. Please try again.');
      return false;
    }

    printingRef.current = true;
    setPrinting(true);
    const result = await executeReceiptPrint(transaction, storeName, previewEl as HTMLElement);
    printingRef.current = false;
    setPrinting(false);

    if (result.ok) {
      const count = transaction.items?.length ?? 0;
      message.success(
        count > 0
          ? `Print dialog opened — ${count} item${count === 1 ? '' : 's'} will match the preview above.`
          : 'Print dialog opened — output will match the preview above.',
      );
      onClose();
    } else {
      message.error(result.error);
    }
    return result.ok;
  }, [transaction, storeName, onClose]);

  if (!transaction) return null;

  const items = transaction.items || [];
  const itemCount = items.length;
  const total = Number(transaction.total_amount) || 0;

  return (
    <Modal
      open={open}
      title={
        <Space size={8} wrap>
          <PrinterOutlined />
          <span>Sale saved — receipt options</span>
          {itemCount > 0 ? (
            <Tag color="blue">
              {itemCount} item{itemCount === 1 ? '' : 's'}
            </Tag>
          ) : (
            <Tag color="warning">No items</Tag>
          )}
          <Tag color="green">{formatNu(total)}</Tag>
        </Space>
      }
      onCancel={handleDoneNoPrint}
      width={500}
      centered
      destroyOnClose
      maskClosable={!printing}
      footer={
        <div className="receipt-print-modal-footer">
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
            <CheckCircleOutlined style={{ marginRight: 6 }} />
            This sale is already saved in the system. Print only if the customer wants a slip.
          </Typography.Text>
          <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button icon={<CloseOutlined />} onClick={handleDoneNoPrint} disabled={printing}>
              Done — no print
            </Button>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={() => void handlePrint()}
              loading={printing}
              disabled={itemCount === 0}
            >
              Print receipt
            </Button>
          </Space>
        </div>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 13 }}>
        Preview below. Choose <strong>Done — no print</strong> to keep the record only, or{' '}
        <strong>Print receipt</strong> to print a copy for the customer.
      </Typography.Paragraph>
      <div ref={previewRef} className="receipt-print-preview-wrap">
        <ReceiptPrintView transaction={transaction} storeName={storeName} />
      </div>
    </Modal>
  );
};
